import { SchemaTypes } from '@allesauto/graphql';
import { consts } from '@allesauto/utils/consts';

import { SET_FACETS } from '@/store/redux/actions/pages/pcp';
import { IReduxActionProps } from '@/store/redux/Utils';

const MAX_OWNER_MAX_DEFAULT =
  consts.PCP.FILTERS.VEHICLE_CONDITION.PREVIOUS_OWNERS_SLIDER_VALUES;
/**
 * FACETS STATE
 *
 * stores display names by name
 * and
 * range defaults
 *
 * by filter state
 */
type DisplayNameByNameType = { [key: string]: string };

export const getDisplayNamesByName = (facets: any) =>
  facets &&
  facets.reduce(
    (
      acc: { [name: string]: string },
      { name, displayName }: { name: string; displayName: string }
    ) =>
      Object.assign(acc, {
        [name]: displayName,
      }),
    {}
  );

export interface FacetsStateProps {
  // TODO: model displayNamesByName
  makeModel: {
    make: DisplayNameByNameType;
  };
  priceRange: {
    [key: string]: SchemaTypes.PriceRange;
  };
  bodyColors: {
    colors: DisplayNameByNameType;
  };
  equipment: {
    comfort: DisplayNameByNameType;
    security: DisplayNameByNameType;
    entertainment: DisplayNameByNameType;
  };
  merchantLocation: {
    merchantLocation: DisplayNameByNameType;
  };
  interior: {
    interiorType: DisplayNameByNameType;
    interiorColor: DisplayNameByNameType;
  };
  fuelType: {
    fuelType: DisplayNameByNameType;
    maxFuelConsumption: number;
    maxPowerConsumption: number;
  };
  drive: {
    gearBox: DisplayNameByNameType;
    power: { min: number; max: number; key: string };
    maxCubicCapacity: number;
  };
  vehicleCondition: {
    condition: DisplayNameByNameType;
    mileage: DisplayNameByNameType;
  };
  vehicleType: {
    vehicleType: DisplayNameByNameType;
    doors: DisplayNameByNameType;
  };
  emission: {
    emissionClass: DisplayNameByNameType;
    emissionSticker: DisplayNameByNameType;
    co2Efficiency: DisplayNameByNameType;
  };
}

export const InitialFacetsState: FacetsStateProps = {
  makeModel: {
    make: {},
  },
  bodyColors: {
    colors: {},
  },
  priceRange: {},
  equipment: {
    comfort: {},
    security: {},
    entertainment: {},
  },
  merchantLocation: {
    merchantLocation: {},
  },
  interior: {
    interiorType: {},
    interiorColor: {},
  },
  fuelType: {
    fuelType: {},
    maxFuelConsumption: 0,
    maxPowerConsumption: 0,
  },
  emission: {
    emissionClass: {},
    emissionSticker: {},
    co2Efficiency: {},
  },
  drive: {
    gearBox: {},
    power: { min: 0, max: 0, key: '' },
    maxCubicCapacity: 0,
  },
  vehicleType: {
    vehicleType: {},
    doors: {},
  },
  vehicleCondition: {
    condition: {},
    mileage: {},
  },
};

const FacetsState = (
  state: FacetsStateProps = InitialFacetsState,
  action: IReduxActionProps
) => {
  switch (action.type) {
    case SET_FACETS: {
      // make and bodyColor facets
      const { make, bodyColor } = action.payload as any;
      // drive facets
      const { gearBox } = action.payload && action.payload.drive;
      // fuelType facets
      const { fuelTypes } = action.payload?.fuelType ?? {};
      // interior facets
      const { interiorType, interiorColor } =
        action.payload && action.payload.interior;
      // vehicleType facets
      const { vehicleType, doors } =
        action.payload && action.payload.vehicleType;
      // condition facets
      const { condition } = action.payload && action.payload.vehicleConditions;
      // TODO: there shouldnt be a need to duplicate a const in the redux store
      const {
        PCP: {
          FILTERS: {
            VEHICLE_CONDITION: { MILEAGE_DROP_DOWN_ITEMS },
          },
        },
      } = consts;
      const parsedMileage: any = {};
      for (const item of MILEAGE_DROP_DOWN_ITEMS) {
        const id = item.value;
        parsedMileage[id] = item.label;
      }
      // emission facets
      const { emissionClass, co2Efficiency, emissionSticker } =
        (action.payload && action.payload.emission) || {};
      // equipment facets
      const {
        comfort: comfortFacets,
        security: securityFacets,
        entertainment: entertainmentFacets,
      } = action.payload && action.payload.equipment;
      // merchantLocation facets
      const { merchantUuids } = action.payload as any;

      return {
        ...state,
        makeModel: {
          make: getDisplayNamesByName(make),
        },
        bodyColors: {
          colors: getDisplayNamesByName(bodyColor),
        },
        interior: {
          interiorType: getDisplayNamesByName(interiorType),
          interiorColor: getDisplayNamesByName(interiorColor),
        },
        fuelType: {
          ...action.payload?.fuelType,
          fuelType: getDisplayNamesByName(fuelTypes),
        },
        drive: {
          ...action.payload?.drive,
          gearBox: getDisplayNamesByName(gearBox),
        },
        priceRange: {
          ...state.priceRange,
          ...action.payload?.price.reduce(
            (
              acc: { [key: string]: SchemaTypes.PriceRange },
              { key, ...range }: { key: string }
            ) =>
              Object.assign(acc, {
                [key]: range,
              }),
            {}
          ),
        },
        vehicleType: {
          vehicleType: getDisplayNamesByName(vehicleType),
          doors: getDisplayNamesByName(doors),
        },
        vehicleCondition: {
          condition: getDisplayNamesByName(condition),
          maxOwners:
            action.payload?.vehicleConditions.maxOwners >
            MAX_OWNER_MAX_DEFAULT.max
              ? MAX_OWNER_MAX_DEFAULT.max
              : action.payload?.vehicleConditions.maxOwners,
          mileage: parsedMileage,
        },
        emission: {
          emissionClass: getDisplayNamesByName(emissionClass),
          co2Efficiency: getDisplayNamesByName(co2Efficiency),
          emissionSticker: getDisplayNamesByName(emissionSticker),
        },
        equipment: {
          comfort: getDisplayNamesByName(comfortFacets),
          security: getDisplayNamesByName(securityFacets),
          entertainment: getDisplayNamesByName(entertainmentFacets),
        },
        merchantLocation: {
          merchantLocation: getDisplayNamesByName(merchantUuids),
        },
      };
    }
    default:
      return state;
  }
};

export default FacetsState;
